import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { PurchaseType } from "./shop";

export type CartItem = {
  key: string;
  productId: string;
  slug: string;
  name: string;
  brand: string | null;
  image: string | null;
  purchaseType: PurchaseType;
  unitPrice: number;
  qty: number;
  options: Record<string, string>;
};

type AddInput = Omit<CartItem, "key" | "qty"> & { qty?: number };

type CartCtx = {
  items: CartItem[];
  count: number;
  subtotal: number;
  add: (item: AddInput) => void;
  remove: (key: string) => void;
  setQty: (key: string, qty: number) => void;
  setPurchaseType: (key: string, purchaseType: PurchaseType, unitPrice: number) => void;
  clear: () => void;
  open: boolean;
  setOpen: (v: boolean) => void;
};

const Ctx = createContext<CartCtx | null>(null);
const KEY = "ns-cart-v2";

function makeKey(i: AddInput) {
  const opts = Object.entries(i.options ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join("|");
  return `${i.productId}__${i.purchaseType}__${opts}`;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {}
  }, [items, hydrated]);

  const add = (input: AddInput) => {
    const key = makeKey(input);
    const qty = input.qty ?? 1;
    setItems((cur) => {
      const found = cur.find((i) => i.key === key);
      if (found) return cur.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [...cur, { ...input, options: input.options ?? {}, key, qty }];
    });
    setOpen(true);
  };

  const remove = (key: string) => setItems((cur) => cur.filter((i) => i.key !== key));

  const setQty = (key: string, qty: number) =>
    setItems((cur) => (qty <= 0 ? cur.filter((i) => i.key !== key) : cur.map((i) => (i.key === key ? { ...i, qty } : i))));

  const setPurchaseType = (key: string, purchaseType: PurchaseType, unitPrice: number) =>
    setItems((cur) => cur.map((i) => (i.key === key ? { ...i, purchaseType, unitPrice } : i)));

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.unitPrice * i.qty, 0);

  return (
    <Ctx.Provider value={{ items, count, subtotal, add, remove, setQty, setPurchaseType, clear, open, setOpen }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCart() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCart must be used inside CartProvider");
  return v;
}
